import _ from 'lodash';
import Factory from 'factories/all';
import moment from 'moment';

import { bindCallbacks, statusText } from 'scripts/infrastructure/backends/fake_backend_http/lib/common';
import { getDatabase } from 'scripts/infrastructure/backends/fake_backend/database';
import { RoutingChannel } from 'models/agent_routing_preferences';

export default class LiveboardService {
  constructor(publishResponse, database = getDatabase) {
    this.getDatabase = database;
  }

  getRoutes() {
    return bindCallbacks({
      '/api/v1/orgs/:orgId/liveboards/:liveboardType': {
        GET: (attrs, callback, path, { orgId, liveboardType }, query) => {
          callback(null, {
            status: 200,
            statusText: statusText(200),
            response: this.fetch(orgId, liveboardType, query),
          });
        },
      },
    });
  }

  fetch(orgId, liveboardType, query) {
    if (_.isEmpty(this.getDatabase(orgId).summaryLiveboard)) {
      return {};
    }
    switch (liveboardType) {
      case 'agents':
        return this.fetchAgentsLiveboard(orgId, query);
      case 'summary':
        return this.fetchSummaryLiveboard(orgId, query);
      case 'topics':
        return this.fetchTopicsLiveboard(orgId, query);
      default:
        throw new Error(`invalid liveboard requested [${liveboardType}]`);
    }
  }

  fetchAgentsLiveboard(orgId, query = {}) {
    let liveboard = this.getDatabase(orgId).summaryLiveboard;
    let agents = this.getDatabase(orgId).agents;
    let agentStatusReasons = this.getDatabase(orgId).agentStatusReasons;
    let activeReasons = _.filter(agentStatusReasons, { type: 'ACTIVE' });
    let awayReasons = _.filter(agentStatusReasons, { type: 'AWAY' });

    const customers = this.getDatabase(orgId).customers;

    function getAgentUtilization(agentId, agentIndex) {
      let availableChannels = [];
      _(RoutingChannel)
        .values()
        .forEach(channel => {
          if (_.sample([true, false])) {
            availableChannels.push(channel);
          }
        });

      switch (agentIndex % 3) {
        case 0:
          return {
            agentId,
            status: 'READY',
            statusReasonId: _.get(_.sample(activeReasons), 'id'),
            availableChannels,
            onCall: _.sample([true, false]),
            activity: _.sample([
              'CUSTOMER',
              'INBOX',
              'KNOWLEDGE_BASE',
              'LIVEBOARDS',
              'SEARCH',
              'CONVERSATION_NOTE',
              'SMS',
            ]),
            activeChats: _.sample([
              [],
              _.sampleSize(
                [
                  {
                    startedAt: moment()
                      .subtract(3, 'minutes')
                      .toISOString(),
                    customerId: customers[0].id,
                  },
                  {
                    startedAt: moment()
                      .subtract(5, 'minutes')
                      .toISOString(),
                    customerId: customers[1].id,
                  },
                  {
                    startedAt: moment()
                      .subtract(8, 'minutes')
                      .toISOString(),
                    customerId: customers[2].id,
                  },
                  {
                    startedAt: moment()
                      .subtract(10, 'minutes')
                      .toISOString(),
                    customerId: customers[3].id,
                  },
                  {
                    startedAt: moment()
                      .subtract(15, 'minutes')
                      .toISOString(),
                    customerId: customers[4].id,
                  },
                  {
                    startedAt: moment()
                      .subtract(12, 'minutes')
                      .subtract(30, 'seconds')
                      .toISOString(),
                    customerId: customers[5].id,
                  },
                ],
                _.random(0, 6)
              ),
            ]),
            statusUpdatedAt: moment()
              .subtract(agentIndex, 'seconds')
              .toISOString(),
            presenceUpdatedAt: moment()
              .subtract(agentIndex, 'seconds')
              .toISOString(),
          };
        case 1:
          return {
            agentId,
            status: 'BUSY',
            statusReasonId: _.get(_.sample(activeReasons), 'id'),
            availableChannels,
            onCall: _.sample([true, false]),
            activity: _.sample(['PHONE', 'EMAIL', 'CONVERSATION_NOTE']),
            statusUpdatedAt: moment()
              .subtract(11, 'minutes')
              .subtract(agentIndex, 'minutes')
              .toISOString(),
            presenceUpdatedAt: moment()
              .subtract(11, 'minutes')
              .subtract(agentIndex, 'minutes')
              .toISOString(),
          };
        case 2:
          return {
            agentId,
            status: 'AWAY',
            availableChannels,
            reasonId: _.get(_.sample(awayReasons), 'id'),
            statusUpdatedAt: moment()
              .subtract(agentIndex, 'minutes')
              .toISOString(),
            presenceUpdatedAt: moment()
              .subtract(agentIndex, 'minutes')
              .toISOString(),
          };
      }
    }

    liveboard.utilization = agents.map(function(agent, index) {
      return getAgentUtilization(agent.id, index);
    });

    if (!query.routingGroupId) {
      return liveboard;
    }

    return { utilization: liveboard.utilization };
  }

  fetchSummaryLiveboard(orgId, payload = {}) {
    let liveboard = this.getDatabase(orgId).summaryLiveboard;

    ['agent', 'conversation'].forEach(function(v) {
      _.forEach(liveboard[v], function(val, key) {
        liveboard[v][key] = randomValueWithMax(val);
      });
    });
    ['channel', 'sla'].forEach(function(v) {
      ['email', 'phone', 'sms', 'fbMessage', 'whatsapp'].forEach(function(c) {
        _.forEach(liveboard[v][c], function(val, key) {
          if (key !== 'oldestUnanswered') {
            liveboard[v][c][key] = randomValueWithMax(val);
          }
        });
      });
    });

    if (!payload.routingGroupId) {
      return liveboard;
    }

    let routingGroupBoard = {};
    _.forEach(liveboard, function(field, key) {
      if (key === 'trend') {
        routingGroupBoard.trend = _.clone(liveboard.trend);
        routingGroupBoard.trend.forEach(function(trend, trendKey) {
          trend.newConversations = randomValueWithMax(trend.newConversations);
          trend.closedConversations = randomValueWithMax(trend.closedConversations);
          routingGroupBoard.trend[trendKey] = trend;
        });
      } else {
        routingGroupBoard[key] = {};
        _.forEach(field, function(val, k) {
          routingGroupBoard[key][k] = typeof val !== 'number' ? val : Math.floor(val / 2);
        });
      }
    });
    return routingGroupBoard;
  }

  fetchTopicsLiveboard(orgId, payload = {}) {
    let topics = this.getDatabase(orgId).topics;

    let lbTopics = topics.map(topic => {
      return Factory.build('TopicsLiveboardTopicWithDefaults', {
        id: topic.id,
        conversations: {
          openAssigned: randomValueWithMax(30, 50, 0.2),
          openUnassigned: randomValueWithMax(30, 50, 0.2),
          waiting: randomValueWithMax(100, 150, 0.2),
          closedToday: randomValueWithMax(200, 300, 0.2),
        },
        currentWait: randomValueWithMax(100000, 1000000, 0.1),
        sla: {
          over: randomValueWithMax(10, 20, 0.1),
          total: randomValueWithMax(100, 120, 0.1),
        },
      });
    });
    lbTopics = [
      Factory.build('TopicsLiveboardTopicWithDefaults', {
        id: null,
        conversations: {
          openAssigned: randomValueWithMax(30, 50, 0.2),
          openUnassigned: randomValueWithMax(30, 50, 0.2),
          waiting: randomValueWithMax(100, 150, 0.2),
          closedToday: randomValueWithMax(200, 300, 0.2),
        },
        currentWait: randomValueWithMax(100000, 1000000, 0.1),
        sla: {
          over: randomValueWithMax(10, 20, 0.1),
          total: randomValueWithMax(100, 120, 0.1),
        },
      }),
    ].concat(lbTopics);

    let totals = Factory.build('TopicsLiveboardTotalsWithDefaults', {
      groupId: payload.routingGroupId ? payload.routingGroupId : null,
      agentsOnline: randomValueWithMax(30, 50, 0.1),
      conversations: {
        openAssigned: randomValueWithMax(600, 1000),
        openUnassigned: randomValueWithMax(600, 1000),
        waiting: randomValueWithMax(1000, 2000),
        closedToday: randomValueWithMax(2000, 3000),
      },
      sla: {
        over: randomValueWithMax(10, 20),
        total: randomValueWithMax(100, 120),
      },
    });
    let lbParams = { topics: lbTopics, totals };

    return Factory.build('TopicsLiveboardWithDefaults', lbParams);
  }
}

function randomValueWithMax(startingValue, maxVal, rangePct = 0.05) {
  let max = Math.floor(startingValue * (1 + rangePct));
  let min = Math.ceil(startingValue * (1 - rangePct));

  let newVal = Math.floor(Math.random() * (max - min + 1)) + min;
  return !maxVal || newVal < maxVal ? newVal : maxVal;
}



// WEBPACK FOOTER //
// ./src/scripts/infrastructure/backends/fake_backend/liveboard_service.js